// Tastenkürzel in einer Schreibweise (design-system §4.0, §6.1): 'mod+k' (mod = ⌘ oder Strg), 'g b' (Folge),
// 'shift+?', 'enter', 'esc'. Einzelbuchstaben wirken nur, wenn kein Eingabefeld den Fokus hat.
import { useEffect, useRef } from 'react'

export type Hotkey = string

export const isMac = (): boolean =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '')

const KEY_LABELS: Record<string, string> = {
  enter: '↵',
  esc: 'Esc',
  escape: 'Esc',
  tab: 'Tab',
  space: 'Leertaste',
  backspace: '⌫',
  delete: 'Entf',
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
}

/** Anzeige passend zum Betriebssystem: 'mod+k' → ['⌘', 'K'] bzw. ['Strg', 'K']; 'g b' → ['G', 'B']. */
export function formatHotkey(hotkey: Hotkey, mac: boolean = isMac()): string[] {
  return hotkey
    .trim()
    .split(/\s+/)
    .flatMap((chord) =>
      chord.split('+').map((part) => {
        const key = part.toLowerCase()
        if (key === 'mod') return mac ? '⌘' : 'Strg'
        if (key === 'shift') return mac ? '⇧' : 'Umschalt'
        if (key === 'alt') return mac ? '⌥' : 'Alt'
        if (key === 'ctrl') return mac ? '⌃' : 'Strg'
        return KEY_LABELS[key] ?? (part.length === 1 ? part.toUpperCase() : part)
      }),
    )
}

/** Für Screenreader: „Befehl K“ statt „⌘K“. */
export function describeHotkey(hotkey: Hotkey, mac: boolean = isMac()): string {
  return hotkey
    .trim()
    .split(/\s+/)
    .map((chord) =>
      chord
        .split('+')
        .map((part) => {
          const key = part.toLowerCase()
          if (key === 'mod') return mac ? 'Befehl' : 'Strg'
          if (key === 'shift') return 'Umschalt'
          if (key === 'alt') return mac ? 'Wahl' : 'Alt'
          if (key === 'enter') return 'Eingabe'
          if (key === 'esc' || key === 'escape') return 'Escape'
          return part.toUpperCase()
        })
        .join(' + '),
    )
    .join(', dann ')
}

interface Chord {
  key: string
  mod: boolean
  shift: boolean
  alt: boolean
}

const ALIASES: Record<string, string> = { esc: 'escape', up: 'arrowup', down: 'arrowdown', left: 'arrowleft', right: 'arrowright', space: ' ' } // prettier-ignore

function parseChord(chord: string): Chord {
  const parts = chord.toLowerCase().split('+')
  const key = parts[parts.length - 1] ?? ''
  return {
    key: ALIASES[key] ?? key,
    mod: parts.includes('mod') || parts.includes('ctrl'),
    shift: parts.includes('shift'),
    alt: parts.includes('alt'),
  }
}

function chordMatches(event: KeyboardEvent, chord: Chord): boolean {
  const mod = event.metaKey || event.ctrlKey
  if (chord.mod !== mod || chord.alt !== event.altKey) return false
  const key = event.key.toLowerCase()
  // Bei Zeichen wie '?' steckt Umschalt schon im Zeichen – dort wird es nicht zusätzlich verlangt.
  const printable = chord.key.length === 1 && !/[a-z0-9]/.test(chord.key)
  if (!printable && chord.shift !== event.shiftKey) return false
  return key === chord.key
}

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

export function matchHotkey(event: KeyboardEvent, hotkey: Hotkey): boolean {
  const chords = hotkey.trim().split(/\s+/)
  return chords.length === 1 && chordMatches(event, parseChord(chords[0] as string))
}

export interface HotkeyOptions {
  enabled?: boolean
  /** auch auslösen, wenn ein Eingabefeld den Fokus hat (Standard: nur bei Kürzeln mit mod) */
  allowInInputs?: boolean
  preventDefault?: boolean
}

const SEQUENCE_TIMEOUT_MS = 1000

/** Registriert ein Kürzel am Dokument, solange die Komponente sichtbar und `enabled` ist. */
export function useHotkey(
  hotkey: Hotkey | undefined,
  handler: (event: KeyboardEvent) => void,
  options: HotkeyOptions = {},
): void {
  const { enabled = true, allowInInputs, preventDefault = true } = options
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    if (!hotkey || !enabled) return
    const chords = hotkey.trim().split(/\s+/).map(parseChord)
    const first = chords[0]
    if (!first) return
    const inInputs = allowInInputs ?? chords.every((c) => c.mod)
    let position = 0
    let timer: ReturnType<typeof setTimeout> | undefined

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.isComposing) return
      if (!inInputs && isEditableTarget(event.target)) return
      const expected = chords[position]
      if (expected && chordMatches(event, expected)) {
        position++
        if (timer) clearTimeout(timer)
        if (position === chords.length) {
          position = 0
          if (preventDefault) event.preventDefault()
          handlerRef.current(event)
        } else {
          timer = setTimeout(() => (position = 0), SEQUENCE_TIMEOUT_MS)
        }
      } else if (position > 0 && !['Shift', 'Meta', 'Control', 'Alt'].includes(event.key)) {
        position = chordMatches(event, first) ? 1 : 0
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (timer) clearTimeout(timer)
    }
  }, [hotkey, enabled, allowInInputs, preventDefault])
}
